import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Clock, HandHelping, Maximize2, Search, StickyNote, Tag, X } from "lucide-react";
import type { ContactDetail } from "@hub/shared";
import { Button, EmptyState, IconButton, Spinner } from "@/ui";
import { useEngineQuery } from "@/engine/hooks";
// ProfileFields renders contact methods and work; the preview skips the sources table, which needs the page width.
import { ContactNetworkSection } from "@/features/network";
import { ContactHeader } from "./ContactHeader";
import { useContactActions } from "./ContactActionHost";
import { Highlights, NoteCard, ProfileFields, ReminderRow, TimelineEntryView } from "./ContactSections";
import s from "../shared.module.css";

/** One line: what this person offers and what they are looking for. Null when neither is recorded. */
export function needsOffersLine(detail: Pick<ContactDetail, "needs" | "services">): { offers: string | null; needs: string | null } | null {
  const offers = detail.services.filter((sv) => sv.isActive).map((sv) => sv.title);
  const needs = detail.needs.filter((n) => !n.isCompleted).map((n) => n.title);
  if (offers.length === 0 && needs.length === 0) return null;
  const join = (xs: string[]) => (xs.length <= 2 ? xs.join(" and ") : `${xs.slice(0, 2).join(", ")} and ${xs.length - 2} more`);
  return { offers: offers.length ? join(offers) : null, needs: needs.length ? join(needs) : null };
}

/** Right-sidebar view of a person: everything at a glance, with a link to the full page. */
export function ContactPreview({ contactId, onClose }: { contactId: string; onClose: () => void }) {
  const navigate = useNavigate();
  const detail = useEngineQuery("getContact", { contactId });
  // Recent activity is what already happened; the next meeting is in the highlights.
  const [now] = useState(() => new Date().toISOString());
  const timeline = useEngineQuery("getContactTimeline", { contactId, filter: { to: now }, limit: 5, offset: 0 });
  const actions = useContactActions(contactId, { register: false, afterArchive: onClose });
  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }} data-testid="contact-preview">
      <div className="u-row" style={{ padding: "8px 10px", borderBottom: "1px solid var(--border)", justifyContent: "space-between" }}>
        <Button size="sm" variant="outline" icon={<Maximize2 size={13} />} onClick={() => navigate(`/contact/${contactId}`)} title="Sources, the full timeline and every note">Open full page</Button>
        <IconButton label="Close sidebar" onClick={onClose}><X size={16} /></IconButton>
      </div>
      <div style={{ flex: 1, overflowY: "auto", padding: 14 }}>
        {detail.isLoading && <Spinner />}
        {detail.isSuccess && !detail.data && <EmptyState title="Not found" />}
        {detail.data && (
          <>
            <ContactHeader detail={detail.data} onAction={actions.open} compact />
            <div className="u-row" style={{ gap: 6, marginTop: 12, flexWrap: "wrap" }}>
              <Button size="sm" icon={<StickyNote size={13} />} onClick={() => actions.open("note")}>Note</Button>
              <Button size="sm" icon={<Bell size={13} />} onClick={() => actions.open("reminder")}>Reminder</Button>
              <Button size="sm" icon={<Tag size={13} />} onClick={() => actions.open("group")}>Group</Button>
              {!detail.data.contact.isMemorialized && <Button size="sm" icon={<Clock size={13} />} onClick={() => actions.open("cadence")}>Cadence</Button>}
            </div>
            {(() => {
              const line = needsOffersLine(detail.data);
              if (!line) return null;
              return (
                <div className="u-col u-small" style={{ gap: 2, marginTop: 10 }} data-testid="preview-needs-offers">
                  {line.offers && <span className="u-row" style={{ gap: 5 }}><HandHelping size={12} className="u-muted" /> Can help with {line.offers}</span>}
                  {line.needs && <span className="u-row" style={{ gap: 5 }}><Search size={12} className="u-muted" /> Looking for {line.needs}</span>}
                </div>
              );
            })()}
            <div style={{ marginTop: 14 }}>
              <Highlights detail={detail.data} />
              <ProfileFields detail={detail.data} compact />
              <section className={s.section}>
                <div style={{ fontWeight: 600, marginBottom: 6 }}>Recent activity</div>
                {timeline.data?.items.length === 0 && <div className="u-muted u-small">No interactions yet.</div>}
                {timeline.data?.items.slice(0, 3).map((e) => <TimelineEntryView key={e.interaction.id} entry={e} />)}
              </section>
              {detail.data.reminders.filter((r) => !r.isProcessed).length > 0 && (
                <section className={s.section}>
                  <div style={{ fontWeight: 600, marginBottom: 6 }}>Reminders</div>
                  {detail.data.reminders.filter((r) => !r.isProcessed).slice(0, 2).map((r) => <ReminderRow key={r.id} reminder={r} />)}
                </section>
              )}
              {detail.data.notes.length > 0 && (
                <section className={s.section}>
                  <div style={{ fontWeight: 600, marginBottom: 6 }}>Latest note</div>
                  {detail.data.notes.slice(0, 1).map((n) => <NoteCard key={n.id} note={n} />)}
                </section>
              )}
              <ContactNetworkSection contactId={contactId} compact />
              <div className="u-small u-muted" style={{ marginTop: 14 }}>
                Sources, the full timeline and every note are on the person page. <Button size="sm" variant="ghost" onClick={() => navigate(`/contact/${contactId}`)}>Open full page</Button>
              </div>
            </div>
          </>
        )}
      </div>
      {actions.modals}
    </div>
  );
}
